import React, { useState } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Box,
  Chip,
  Button,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
  Avatar,
  useTheme,
  alpha
} from '@mui/material';
import {
  Person as PersonIcon,
  VideoCall as VideoCallIcon,
  Schedule as ScheduleIcon,
  CheckCircle as CheckCircleIcon,
  AccessTime as AccessTimeIcon,
  Assignment as AssignmentIcon,
  CalendarToday as CalendarIcon,
  Visibility as VisibilityIcon
} from '@mui/icons-material';
import PropTypes from 'prop-types';

function AppointmentCard({ 
  appointment, 
  onConfirm, 
  onComplete, 
  onStartVideoCall,
  onViewDetails,
  isConfirmed = false,
  isCompleted = false 
}) {
  const theme = useTheme();
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [completeDialogOpen, setCompleteDialogOpen] = useState(false);

  const handleConfirmClick = () => {
    setConfirmDialogOpen(true);
  };

  const handleCompleteClick = () => {
    setCompleteDialogOpen(true);
  };

  const handleConfirmAppointment = () => {
    onConfirm(appointment.requestId);
    setConfirmDialogOpen(false);
  };

  const handleCompleteAppointment = () => {
    onComplete(appointment);
    setCompleteDialogOpen(false);
  };

  const handleStartVideoCall = () => {
    onStartVideoCall(appointment);
  };

  const handleViewDetails = () => {
    onViewDetails(appointment);
  };

  // Get status info for display
  const getStatusInfo = () => {
    const status = appointment.status?.toUpperCase();
    switch (status) {
      case 'SCHEDULED':
        return { 
          label: 'Scheduled', 
          color: 'warning',
          bgColor: alpha(theme.palette.warning.main, 0.1),
          textColor: theme.palette.warning.main
        };
      case 'REQUESTED':
        return { 
          label: 'Requested', 
          color: 'warning',
          bgColor: alpha(theme.palette.warning.main, 0.1),
          textColor: theme.palette.warning.main
        };
      case 'CONFIRMED':
        return { 
          label: 'Confirmed', 
          color: 'success',
          bgColor: alpha(theme.palette.success.main, 0.1),
          textColor: theme.palette.success.main
        };
      case 'ONGOING':
        return { 
          label: 'Ongoing', 
          color: 'primary',
          bgColor: alpha(theme.palette.primary.main, 0.1),
          textColor: theme.palette.primary.main
        };
      case 'COMPLETED':
        return { 
          label: 'Completed', 
          color: 'info',
          bgColor: alpha(theme.palette.info.main, 0.1),
          textColor: theme.palette.info.main
        };
      case 'CANCELLED':
        return { 
          label: 'Cancelled', 
          color: 'error',
          bgColor: alpha(theme.palette.error.main, 0.1),
          textColor: theme.palette.error.main
        };
      default:
        return { 
          label: status || 'Unknown', 
          color: 'default',
          bgColor: alpha(theme.palette.grey[500], 0.1),
          textColor: theme.palette.grey[600]
        };
    }
  };

  const statusInfo = getStatusInfo();
  const isScheduledStatus = appointment.status?.toUpperCase() === 'SCHEDULED';
  const isRequestedStatus = appointment.status?.toUpperCase() === 'REQUESTED';
  const isConfirmedStatus = appointment.status?.toUpperCase() === 'CONFIRMED';
  const isOngoingStatus = appointment.status?.toUpperCase() === 'ONGOING';
  const isCompletedStatus = appointment.status?.toUpperCase() === 'COMPLETED';

  // Format date and time functions

  const formatDate = (dateString) => {
    if (!dateString) return 'Not specified';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const formatTime = (dateString) => {
    if (!dateString) return 'Not specified';
    const date = new Date(dateString);
    return date.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <>
      <Card 
        variant="outlined" 
        sx={{ 
          mb: 2,
          borderRadius: 2,
          transition: 'all 0.2s ease-in-out',
          '&:hover': {
            boxShadow: theme.shadows[4],
            transform: 'translateY(-2px)'
          },
          borderLeft: `4px solid ${statusInfo.textColor}`,
          bgcolor: statusInfo.bgColor
        }}
      >
        <CardContent>
          {/* Header Row */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', flex: 1 }}>
              <Avatar sx={{ bgcolor: theme.palette.primary.main, mr: 2 }}>
                <PersonIcon />
              </Avatar>
              <Box sx={{ flex: 1 }}>
                <Typography variant="h6" component="h3" sx={{ fontWeight: 600, mb: 0.5 }}>
                  {appointment.patientName || `Patient ${appointment.userId?.slice(-8) || 'Unknown'}`}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Request ID: {appointment.requestId?.slice(-8) || 'Unknown'}
                </Typography>
              </Box>
            </Box>
            
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Chip 
                label={statusInfo.label}
                color={statusInfo.color}
                size="small"
                sx={{ fontWeight: 'bold' }}
              />
              <Button
                variant="outlined"
                size="small"
                startIcon={<VisibilityIcon />}
                onClick={handleViewDetails}
                sx={{ borderRadius: 2 }}
              >
                View Details
              </Button>
            </Box>
          </Box>

          {/* Appointment Info Row */}
          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item xs={12} sm={6}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <CalendarIcon fontSize="small" sx={{ mr: 1, color: theme.palette.primary.main }} />
                <Typography variant="body2">
                  <strong>Requested:</strong> {formatDate(appointment.requestedAt || appointment.createdAt)}
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <AccessTimeIcon fontSize="small" sx={{ mr: 1, color: theme.palette.primary.main }} />
                <Typography variant="body2">
                  <strong>Time:</strong> {formatTime(appointment.requestedAt || appointment.createdAt)}
                </Typography>
              </Box>
            </Grid>
            {appointment.confirmedSlot && (
              <>
                <Grid item xs={12} sm={6}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <CheckCircleIcon fontSize="small" sx={{ mr: 1, color: theme.palette.success.main }} />
                    <Typography variant="body2">
                      <strong>Confirmed:</strong> {formatDate(appointment.confirmedSlot)}
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <ScheduleIcon fontSize="small" sx={{ mr: 1, color: theme.palette.success.main }} />
                    <Typography variant="body2">
                      <strong>Slot:</strong> {formatTime(appointment.confirmedSlot)}
                    </Typography>
                  </Box>
                </Grid>
              </>
            )}
          </Grid>

          {/* Action Buttons Row */}
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            {isScheduledStatus && (
              <Button
                variant="contained"
                color="primary"
                size="small"
                startIcon={<VisibilityIcon />}
                onClick={handleViewDetails}
                sx={{ borderRadius: 2 }}
              >
                Appointment Details
              </Button>
            )}
            
            {isRequestedStatus && (
              <Button
                variant="contained"
                color="success"
                size="small"
                startIcon={<CheckCircleIcon />}
                onClick={handleConfirmClick}
                sx={{ borderRadius: 2 }}
              >
                Confirm Appointment
              </Button>
            )}
            
            {isConfirmedStatus && (
              <>
                <Button
                  variant="contained"
                  color="primary"
                  size="small"
                  startIcon={<VideoCallIcon />}
                  onClick={handleStartVideoCall}
                  sx={{ borderRadius: 2 }}
                >
                  Start Video Call
                </Button>
                <Button
                  variant="outlined"
                  color="info"
                  size="small"
                  startIcon={<AssignmentIcon />}
                  onClick={handleCompleteClick}
                  sx={{ borderRadius: 2 }}
                >
                  Mark Complete
                </Button>
              </>
            )}

            {isOngoingStatus && (
              <>
                <Button
                  variant="contained"
                  color="primary"
                  size="small"
                  startIcon={<VideoCallIcon />}
                  onClick={handleStartVideoCall}
                  sx={{ borderRadius: 2 }}
                >
                  Join Video Call
                </Button>
                <Button
                  variant="outlined"
                  color="info"
                  size="small"
                  startIcon={<AssignmentIcon />}
                  onClick={handleCompleteClick}
                  sx={{ borderRadius: 2 }}
                >
                  Mark Complete
                </Button>
              </>
            )}

            {isCompletedStatus && (
              <Chip 
                label="Appointment Completed" 
                color="info" 
                variant="outlined"
                size="small"
                sx={{ fontWeight: 'bold' }}
              />
            )}
          </Box>


        </CardContent>
      </Card>

      {/* Confirm Dialog */}
      <Dialog
        open={confirmDialogOpen}
        onClose={() => setConfirmDialogOpen(false)}
        aria-labelledby="confirm-dialog-title"
      >
        <DialogTitle id="confirm-dialog-title">
          Confirm Appointment
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to confirm this appointment with {appointment.patientName || 'the patient'}?
            This will send a confirmation notification to the patient.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDialogOpen(false)}>
            Cancel
          </Button>
          <Button onClick={handleConfirmAppointment} variant="contained" color="success">
            Confirm Appointment
          </Button>
        </DialogActions>
      </Dialog>

      {/* Complete Dialog */}
      <Dialog
        open={completeDialogOpen}
        onClose={() => setCompleteDialogOpen(false)}
        aria-labelledby="complete-dialog-title"
      >
        <DialogTitle id="complete-dialog-title">
          Mark Appointment as Complete
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to mark this appointment as completed?
            This action will finalize the appointment record.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCompleteDialogOpen(false)}>
            Cancel
          </Button>
          <Button onClick={handleCompleteAppointment} variant="contained" color="info">
            Mark Complete
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

AppointmentCard.propTypes = {
  appointment: PropTypes.shape({
    requestId: PropTypes.string.isRequired,
    userId: PropTypes.string.isRequired,
    patientName: PropTypes.string,
    status: PropTypes.string.isRequired,
    requestedAt: PropTypes.string,
    createdAt: PropTypes.string,
    confirmedSlot: PropTypes.string,
    confirmedAt: PropTypes.string,
    completedAt: PropTypes.string,
    assignedDoctorId: PropTypes.string,
    appointmentType: PropTypes.string,
    duration: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    userComments: PropTypes.string,
    doctorComments: PropTypes.string,
    patientInfo: PropTypes.shape({
      age: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      gender: PropTypes.string,
      email: PropTypes.string,
      phone: PropTypes.string,
      address: PropTypes.string,
      chronicConditions: PropTypes.array,
      allergies: PropTypes.array
    })
  }).isRequired,
  onConfirm: PropTypes.func.isRequired,
  onComplete: PropTypes.func.isRequired,
  onStartVideoCall: PropTypes.func.isRequired,
  onViewDetails: PropTypes.func.isRequired,
  isConfirmed: PropTypes.bool,
  isCompleted: PropTypes.bool
};

export default AppointmentCard; 